import React from 'react';
import { Play, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';

const PLACEHOLDER_LIGHT = '/assets/placeholders/video_default_light.jpg';

const LatestMessage_Dynamic = React.memo(({ latestVideo, onWatch, loading = false }) => {
  const { isDark } = useTheme();
  
  if (loading) {
    return (
      <div className={`py-10 px-6 transition-colors duration-200 ${isDark ? 'bg-[#333333]' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl md:text-3xl font-bold tracking-tight ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>Dernier Message</h2>
          </div>
          <Card className={`overflow-hidden rounded-2xl ${isDark ? 'bg-[#3A3A3A] border-[#444444]' : ''}`}>
            <CardContent className="p-8 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="w-8 h-8 animate-spin text-pcc-primary" />
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Chargement du dernier message...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!latestVideo) {
    return (
      <div className={`py-10 px-6 transition-colors duration-200 ${isDark ? 'bg-[#333333]' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl md:text-3xl font-bold tracking-tight ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>Dernier Message</h2>
          </div>
          <Card className={`overflow-hidden rounded-2xl ${isDark ? 'bg-[#3A3A3A] border-[#444444]' : ''}`}>
            <CardContent className="p-8 text-center">
              <img loading="lazy" 
                src={PLACEHOLDER_LIGHT}
                alt="Aucune vidéo"
                className="w-32 h-32 object-cover mx-auto mb-4 rounded-xl opacity-50"
              />
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Aucun message disponible pour le moment.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return null;
    }
  };

  const thumbnailUrl = latestVideo.thumbnail || PLACEHOLDER_LIGHT;
  const publishDate = formatDate(latestVideo.publishDate);

  return (
    <div className={`py-10 px-6 transition-colors duration-200 ${isDark ? 'bg-[#333333]' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-2xl md:text-3xl font-bold tracking-tight ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>Dernier Message</h2>
          {publishDate && (
            <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{publishDate}</span>
          )}
        </div>
        
        <Card className={`overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group ${isDark ? 'bg-[#3A3A3A] border-[#444444]' : ''}`}>
          <CardContent className="p-0">
            <div className="grid md:grid-cols-5 gap-0">
              <div className="md:col-span-2 relative cursor-pointer" onClick={onWatch}>
                <img loading="lazy"
                  src={thumbnailUrl}
                  alt={latestVideo.title}
                  className="w-full h-full object-cover min-h-[220px] group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = PLACEHOLDER_LIGHT;
                  }}
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-all flex items-center justify-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play size={28} className="text-pcc-primary ml-1" fill="#9398DE" />
                  </div>
                </div>
                {latestVideo.duration && (
                  <div className="absolute bottom-3 right-3 bg-black/80 px-3 py-1.5 rounded-lg text-white text-xs flex items-center space-x-1.5 font-medium">
                    <Clock size={12} />
                    <span>{latestVideo.duration}</span>
                  </div>
                )}
              </div>

              <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-center">
                <h3 className={`text-xl md:text-2xl font-bold mb-3 line-clamp-2 tracking-tight ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>
                  {latestVideo.title}
                </h3>
                {latestVideo.speaker && (
                  <p className="text-pcc-primary font-medium mb-3">Par {latestVideo.speaker}</p>
                )}
                <p className={`text-sm mb-6 line-clamp-2 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {latestVideo.description}
                </p>
                <Button
                  onClick={onWatch}
                  className="bg-pcc-primary hover:bg-pcc-primary-dark text-white font-semibold w-full md:w-auto px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                  data-testid="watch-latest-btn"
                >
                  Regarder maintenant
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
});

export default LatestMessage_Dynamic;
